import React, { useState, useEffect, useCallback } from 'react';
import { S } from '../styles';
import { getTheme } from '../theme';
import { API, authHeaders } from '../utils/helpers';
import FittingsCalendar from './FittingsCalendar';
import RentalsCalendar from './RentalsCalendar';
import BookingsOverview from './BookingsOverview';
import CustomerMatch from './CustomerMatch';
import { ContactEdit, ConfirmTextLine, NoPhoneHint } from './BookingContact';
import { toastNotified } from '../utils/toast';

// Staff Bookings module — date-grouped list of drop-offs + manual booking
// creation (phone bookings). Backend: /book/staff/*.
//
// Two tabs since 3 Oct: Drop-offs (this list) and Fittings (FittingsCalendar,
// appointments booked to a time and a fitter). The Fittings tab appears once
// a booking package is marked Appointment in Settings. A third tab, Rentals
// (RentalsCalendar), since 4 Oct: it appears once the rental fleet has a bike.

function fmtPhone(p) {
  const d = (p || '').replace(/\D/g, '').slice(-10);
  if (d.length !== 10) return p || '';
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function dateHeader(dateStr) {
  const today = new Date().toLocaleDateString('en-CA');
  const tomorrow = new Date(Date.now() + 86400000).toLocaleDateString('en-CA');
  const [y, m, d] = dateStr.split('-').map(Number);
  const label = new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  if (dateStr === today) return `Today · ${label}`;
  if (dateStr === tomorrow) return `Tomorrow · ${label}`;
  return label;
}

function initials(name) {
  const parts = (name || '').trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '#';
}

const EMPTY_FORM = { customerName: '', customerPhone: '', customerEmail: '', itemID: '', customName: '', customPrice: '', dropoffDate: '', bikeDescription: '', notes: '' };

function Bookings({ theme = 'dark', currentUser = null }) {
  const T = getTheme(theme);
  const isDark = theme === 'dark';
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming'); // upcoming | all | canceled
  const [showForm, setShowForm] = useState(false);
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  // Always opens on the Overview (6 Oct, Manny): the high-level view first.
  const [tab, setTab] = useState('overview');
  // A booking opened from the Overview: its tab shows it selected.
  const [focus, setFocus] = useState(null);
  const chooseTab = (t) => { setTab(t); setFocus(null); };
  const [counts, setCounts] = useState(null);
  const loadCounts = useCallback(async () => {
    try {
      const r = await fetch(`${API}/book/overview/staff?days=7`, { credentials: 'include', headers: authHeaders() });
      const d = await r.json();
      if (r.ok) setCounts(d.counts);
    } catch (_) {}
  }, []);
  useEffect(() => { loadCounts(); }, [loadCounts, tab]);
  const openFromOverview = (e) => {
    const t = e.kind === 'fitting' ? 'fittings' : e.kind === 'rental' ? 'rentals' : 'dropoffs';
    setTab(t);
    setFocus({ id: e.id, date: e.kind === 'rental' ? (e.start || e.date) : e.date });
    if (t === 'dropoffs' && filter !== 'upcoming') setFilter('upcoming');
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Drop-offs only: fits have their own calendar on the Fittings tab.
      const params = new URLSearchParams({ kind: 'dropoff' });
      if (filter === 'upcoming') {
        params.set('fromDate', new Date().toLocaleDateString('en-CA'));
      } else if (filter === 'canceled') {
        params.set('status', 'canceled');
      }
      const res = await fetch(`${API}/book/staff/list?${params}`, { credentials: 'include', headers: authHeaders() });
      const data = await res.json();
      let rows = Array.isArray(data.bookings) ? data.bookings : [];
      if (filter === 'upcoming') rows = rows.filter(b => b.status !== 'canceled');
      setBookings(rows);
    } catch (_) {}
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // Reset scroll position on entry — S.main's scroll carries over from
  // whatever module the user was in before.
  useEffect(() => {
    window.scrollTo(0, 0);
    document.querySelector('main')?.scrollTo?.(0, 0);
  }, []);

  useEffect(() => {
    fetch(`${API}/book/staff/packages`, { credentials: 'include', headers: authHeaders() })
      .then(r => r.json())
      .then(d => setPackages(Array.isArray(d.packages) ? d.packages : []))
      .catch(() => {});
    fetch(`${API}/book/staff/settings`, { credentials: 'include', headers: authHeaders() })
      .then(r => r.json())
      .then(d => setBookingSettings(d.booking || {}))
      .catch(() => {});
  }, []);

  const [bookingSettings, setBookingSettings] = useState({});
  const [reschedulingId, setReschedulingId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');

  const reschedule = async (id) => {
    if (!rescheduleDate) return;
    if (!confirmOpenDay(rescheduleDate)) return;
    try {
      const res = await fetch(`${API}/book/staff/${id}/reschedule`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ dropoffDate: rescheduleDate }),
      });
      if (res.ok) {
        setReschedulingId(null);
        setRescheduleDate('');
        load();
      }
    } catch (_) {}
  };

  // Warns (but does not block) when a chosen date falls on a day the shop
  // doesn't normally accept drop-offs. Staff can override deliberately.
  const confirmOpenDay = (dateStr) => {
    const openDays = bookingSettings.openDays || [2, 3, 4, 5, 6];
    const [y, m, d] = dateStr.split('-').map(Number);
    const dow = new Date(y, m - 1, d).getDay();
    if (!openDays.includes(dow)) {
      const dayName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dow];
      return window.confirm(`${dayName} isn't a normal drop-off day for the shop. Book it anyway?`);
    }
    return true;
  };

  const setStatus = async (booking, status) => {
    const id = booking.id;
    if (status === 'canceled') {
      let msg = 'Cancel this booking? The Lightspeed ticket will be moved to your cancellation status (not deleted).';
      const amt = Number(booking.item_price || 0).toFixed(2);
      if (booking.payment_status === 'captured') {
        msg = `Cancel this booking? The Lightspeed ticket will be moved to your cancellation status (not deleted), and the customer's $${amt} deposit will be refunded.`;
      } else if (booking.payment_status === 'authorized') {
        msg = `Cancel this booking? The Lightspeed ticket will be moved to your cancellation status (not deleted). The customer's pending $${amt} authorization will be released — no charge was ever made.`;
      }
      msg += '\n\nThe customer will be notified by text and email.';
      if (!window.confirm(msg)) return;
    }
    try {
      const res = await fetch(`${API}/book/staff/${id}/status`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        if (status === 'canceled') { const d = await res.json().catch(() => ({})); toastNotified(booking.customer_name, d.notified, 'the cancellation'); }
        load();
      }
    } catch (_) {}
  };

  // The Lightspeed customer picked from Possible matches, if any (18 Oct).
  const [pickedCustomer, setPickedCustomer] = useState(null);

  const submitForm = async () => {
    setFormError('');
    if (!form.customerName.trim()) return setFormError('Customer name is required.');
    if (!form.dropoffDate) return setFormError('Drop-off date is required.');
    if (!form.itemID && !form.customName.trim()) return setFormError('Pick a service or enter a custom one.');
    if (!confirmOpenDay(form.dropoffDate)) return;
    setSaving(true);
    try {
      const body = {
        customerName: form.customerName.trim(),
        customerPhone: form.customerPhone.trim(),
        customerEmail: form.customerEmail.trim(),
        dropoffDate: form.dropoffDate,
        bikeDescription: form.bikeDescription.trim(),
        notes: form.notes.trim(),
      };
      if (pickedCustomer?.id) body.lsCustomerId = String(pickedCustomer.id);
      if (form.itemID) body.itemID = form.itemID;
      else { body.itemName = form.customName.trim(); body.itemPrice = parseFloat(form.customPrice) || 0; }

      const res = await fetch(`${API}/book/staff/create`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setFormError(data.error || 'Failed to create booking.');
      } else {
        setForm(EMPTY_FORM);
        setPickedCustomer(null);
        setShowForm(false);
        load();
      }
    } catch (_) {
      setFormError('Failed to create booking.');
    }
    setSaving(false);
  };

  const inputStyle = { background: T.input, border: `1px solid ${T.inputBorder}`, borderRadius: 6, padding: '8px 12px', color: T.text, fontSize: 13, fontFamily: 'Arial, sans-serif', outline: 'none', boxSizing: 'border-box' };

  // Group by date
  const grouped = {};
  bookings.forEach(b => {
    const key = (typeof b.dropoff_date === 'string' ? b.dropoff_date : new Date(b.dropoff_date).toISOString()).slice(0, 10);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(b);
  });
  const dates = Object.keys(grouped).sort();

  const statusBadge = (b) => {
    // Payment refund takes priority — it's the more surprising state to miss,
    // and can coexist with any booking status (a refund can happen without
    // canceling, via the standalone Payments page action).
    if (['refunded', 'voided'].includes(b.payment_status)) return { label: 'refunded', color: '#7a8ba8' };
    if (b.status === 'arrived') return { label: 'arrived', color: '#2ecc71' };
    if (b.status === 'canceled') return { label: 'canceled', color: '#e74c3c' };
    return null;
  };

  const hasFittings = (bookingSettings.enabledItems || []).some(p => p.kind === 'appointment');
  const hasRentals = (((bookingSettings.rentals || {}).fleet) || []).length > 0;
  const active = tab === 'overview' ? 'overview' : (tab === 'fittings' && hasFittings) ? 'fittings' : (tab === 'rentals' && hasRentals) ? 'rentals' : 'dropoffs';
  const onOverview = active === 'overview';
  const onFittings = active === 'fittings';
  const onRentals = active === 'rentals';
  const onDropoffs = active === 'dropoffs';
  const tabBtn = (id, label) => (
    <button type="button" onClick={() => chooseTab(id)} aria-pressed={id === active}
      style={{ fontSize: 14, fontWeight: 700, padding: '7px 16px', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
        background: id === active ? (isDark ? '#e67e22' : '#1a2336') : 'transparent',
        color: id === active ? '#fff' : T.textSub }}>{label}</button>
  );

  return (
    <div>
      <div style={{ ...S.topbar, background: T.topbar, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ ...S.pageTitle, color: T.text }}>Bookings</div>
          <span style={{ display: 'inline-flex', border: `1px solid ${T.inputBorder}`, borderRadius: 9, overflow: 'hidden' }}>
            {tabBtn('overview', 'Overview')}{tabBtn('dropoffs', 'Drop-offs')}{hasFittings && tabBtn('fittings', 'Fittings')}{hasRentals && tabBtn('rentals', 'Rentals')}
          </span>
          {onOverview && counts && <span style={{ fontSize: 13, color: T.textSub }}>{counts.week} in the next 7 days</span>}
          {!loading && onDropoffs && <span style={{ fontSize: 13, color: T.textSub }}>{bookings.length} {filter === 'upcoming' ? 'upcoming' : filter}</span>}
          {onFittings && counts && <span style={{ fontSize: 13, color: T.textSub }}>{counts.upcoming.fitting} upcoming</span>}
          {onRentals && counts && <span style={{ fontSize: 13, color: T.textSub }}>{counts.upcoming.rental} upcoming or out</span>}
        </div>
        {onDropoffs && <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select value={filter} onChange={e => setFilter(e.target.value)}
            style={{ ...inputStyle, padding: '7px 10px' }}>
            <option value="upcoming">Upcoming</option>
            <option value="all">All</option>
            <option value="canceled">Canceled</option>
          </select>
          <button style={{ ...S.btn(true), background: '#e67e22', color: '#fff' }} onClick={() => setShowForm(v => !v)}>
            {showForm ? 'Close' : '+ New booking'}
          </button>
        </div>}
      </div>

      {onOverview && <BookingsOverview T={T} isDark={isDark} onOpen={openFromOverview} onCounts={setCounts} />}
      {onFittings && <FittingsCalendar theme={theme} currentUser={currentUser} focus={focus} />}
      {onRentals && <RentalsCalendar theme={theme} currentUser={currentUser} focus={focus} />}
      {onDropoffs && <>

      {/* Manual booking form */}
      {showForm && (
        <div style={{ ...S.settingsCard, background: T.card, border: `1px solid ${T.cardBorder}` }}>
          <div style={{ ...S.settingsCardTitle, color: T.text }}>New phone booking</div>
          <div style={{ ...S.settingsCardDesc, color: T.textSub }}>
            Creates the Lightspeed customer, bike, and ticket exactly like an online booking, and sends the customer a confirmation. Not limited by the online daily cap.
          </div>
          <CustomerMatch T={T} isDark={isDark} input={{ ...inputStyle, width: '100%' }} form={form} setForm={setForm}
            selected={pickedCustomer} onSelect={setPickedCustomer} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <input style={inputStyle} type="date" value={form.dropoffDate}
              min={new Date().toLocaleDateString('en-CA')}
              onChange={e => setForm({ ...form, dropoffDate: e.target.value })} />
            <select style={inputStyle} value={form.itemID}
              onChange={e => setForm({ ...form, itemID: e.target.value })}>
              <option value="">Custom service…</option>
              {packages.map(p => <option key={p.itemID} value={p.itemID}>{p.name}</option>)}
            </select>
            {!form.itemID ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <input style={{ ...inputStyle, flex: 2 }} placeholder="Custom service name *" value={form.customName}
                  onChange={e => setForm({ ...form, customName: e.target.value })} />
                <input style={{ ...inputStyle, flex: 1 }} placeholder="Price" type="number" min="0" step="0.01" value={form.customPrice}
                  onChange={e => setForm({ ...form, customPrice: e.target.value })} />
              </div>
            ) : <div />}
            <input style={inputStyle} placeholder="Bike — e.g. Trek Domane SL6, red" value={form.bikeDescription}
              onChange={e => setForm({ ...form, bikeDescription: e.target.value })} />
            <input style={inputStyle} placeholder="Notes" value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div style={{ marginTop: -4, marginBottom: 10 }}><NoPhoneHint phone={form.customerPhone} /></div>
          {formError && <div style={{ fontSize: 13, color: '#e74c3c', marginBottom: 10 }}>{formError}</div>}
          <button style={{ ...S.btn(true), background: T.btnPrimary, color: T.btnPrimaryText, opacity: saving ? 0.6 : 1 }}
            disabled={saving} onClick={submitForm}>
            {saving ? 'Creating…' : 'Create booking'}
          </button>
        </div>
      )}

      {/* List */}
      {loading && <div style={{ color: T.textSub, padding: 40 }}>Loading bookings…</div>}
      {!loading && dates.length === 0 && (
        <div style={{ color: T.textSub, padding: 40, textAlign: 'center' }}>
          No {filter === 'upcoming' ? 'upcoming ' : ''}bookings{filter === 'canceled' ? ' canceled' : ''}.
        </div>
      )}
      {!loading && dates.map(date => (
        <div key={date} style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px 2px' }}>
            {dateHeader(date)}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {grouped[date].map(b => {
              const badge = statusBadge(b);
              return (
                <div key={b.id} ref={focus && focus.id === b.id ? (el => { if (el && !el.dataset.seen) { el.dataset.seen = '1'; el.scrollIntoView({ block: 'center' }); } }) : undefined}
                  style={{ background: T.card, border: focus && focus.id === b.id ? '2px solid #e67e22' : `1px solid ${T.cardBorder}`, boxShadow: focus && focus.id === b.id ? '0 0 0 4px #e67e2233' : 'none', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: isDark ? '#243050' : '#e6eef8', color: isDark ? '#8ab0dc' : '#2b6cb0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
                    {initials(b.customer_name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{b.customer_name}</span>
                      {badge && (
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 10, background: `${badge.color}22`, color: badge.color }}>{badge.label}</span>
                      )}
                      {b.customer_match === 'created' && b.status !== 'canceled' && (
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 10, background: '#e67e2222', color: '#e67e22' }}>new customer</span>
                      )}
                      {b.source === 'staff' && (
                        <span style={{ fontSize: 11, padding: '2px 9px', borderRadius: 10, border: `1px solid ${T.border}`, color: T.textSub }}>phone booking</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12.5, color: T.textSub, marginTop: 3 }}>
                      {b.item_name}
                      {b.bike_description ? ` · ${b.bike_description}` : ''}
                      {b.customer_phone ? ` · ${fmtPhone(b.customer_phone)}` : ''}
                    </div>
                    {b.status !== 'canceled' && (
                      <div style={{ marginTop: 3 }}>
                        <ConfirmTextLine T={T} phone={b.customer_phone}
                          confirmText={b.confirm_text ? { status: b.confirm_text, note: b.confirm_text_note, at: b.confirm_text_at } : null} />
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                    {b.ls_workorder_id && (
                      <span style={{ fontSize: 12, color: T.textSub, fontFamily: "'IBM Plex Mono', monospace" }}>#{b.ls_workorder_id}</span>
                    )}
                    {reschedulingId === b.id ? (
                      <>
                        <input type="date" value={rescheduleDate}
                          min={new Date().toLocaleDateString('en-CA')}
                          onChange={e => setRescheduleDate(e.target.value)}
                          style={{ background: T.input, border: `1px solid ${T.inputBorder}`, borderRadius: 6, padding: '4px 8px', color: T.text, fontSize: 12, outline: 'none' }} />
                        <button style={{ ...S.btn(true), fontSize: 12, padding: '5px 12px', background: '#e67e22', color: '#fff' }}
                          onClick={() => reschedule(b.id)}>Save</button>
                        <button style={{ ...S.btn(false), fontSize: 12, padding: '5px 12px', color: T.textSub, borderColor: T.border }}
                          onClick={() => { setReschedulingId(null); setRescheduleDate(''); }}>×</button>
                      </>
                    ) : (
                      <>
                        {b.status !== 'canceled' && (
                          <button style={{ ...S.btn(false), fontSize: 12, padding: '5px 12px', color: T.textSub, borderColor: T.border }}
                            onClick={() => setEditing({ id: b.id, customerName: b.customer_name, customerPhone: b.customer_phone, customerEmail: b.customer_email, workorderId: b.ls_workorder_id })}>Edit</button>
                        )}
                        {b.status === 'confirmed' && (
                          <>
                            <button style={{ ...S.btn(false), fontSize: 12, padding: '5px 12px', color: '#2ecc71', borderColor: '#2ecc7133' }}
                              onClick={() => setStatus(b, 'arrived')}>Arrived</button>
                            <button style={{ ...S.btn(false), fontSize: 12, padding: '5px 12px', color: T.textSub, borderColor: T.border }}
                              onClick={() => { setReschedulingId(b.id); setRescheduleDate(''); }}>Reschedule</button>
                            <button style={{ ...S.btn(false), fontSize: 12, padding: '5px 12px', color: '#e74c3c', borderColor: '#e74c3c33' }}
                              onClick={() => setStatus(b, 'canceled')}>Cancel</button>
                          </>
                        )}
                        {b.status === 'arrived' && (
                          <button style={{ ...S.btn(false), fontSize: 12, padding: '5px 12px', color: T.textSub, borderColor: T.border }}
                            onClick={() => setStatus(b, 'confirmed')}>Undo</button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {editing && <ContactEdit T={T} booking={editing} onClose={() => setEditing(null)} onSaved={() => load()} />}
      </>}
    </div>
  );
}

export default Bookings;